import { Box, Button, Flex, Group, NumberInput, Select, SimpleGrid, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useBulkUpdateWorkPeriods } from '../../api/work-periods-client/work-periods-client.hooks';
import type { BulkUpdateWorkPeriodsPayload } from '../../api/work-periods-client/work-periods-client.type';
import { SidebarContext } from '../../containers/side-bar/side-bar.context';
import { useWorkPeriodsContext } from '../../containers/work-periods/work-periods.context';
import { formatDate, formatYearDate, safeParseDateInput } from '../../helpers/date-time-helpers';
import { getCurrency } from '../../helpers/currency-helpers';
import { useGlobalStore } from '../../store/global-store/global-store';
import { newCOLORS } from '../../styles/colors';
import { baseWhite } from '../../styles/design-tokens';
import { Text } from '../../ui-library/typography/typography';

function EditFinancialDataFormBySprint() {
  const { viewAreaWidth } = useContext(SidebarContext);
  const { selectedBoardId, workPeriods = [], formattedBoards = [], handleBoardSelect } = useWorkPeriodsContext();
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode, symbol: currencySymbol } = getCurrency(organization);

  // State to manage budget values for each work period
  const [budgetValues, setBudgetValues] = useState<Record<string, number | undefined>>({});
  const [originalBudgetValues, setOriginalBudgetValues] = useState<Record<string, number | undefined>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  // Generate year options from work periods
  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    workPeriods.forEach((workPeriod) => {
      const year = dayjs(workPeriod.start_date).year();
      years.add(year);
    });

    const sortedYears = Array.from(years).sort((a, b) => b - a); // Most recent first
    return sortedYears.map((year) => ({
      value: year.toString(),
      label: year.toString(),
    }));
  }, [workPeriods]);

  // Set default year to current year if available in options
  useEffect(() => {
    if (yearOptions.length > 0 && !selectedYear) {
      const currentYear = dayjs().year().toString();
      const hasCurrentYear = yearOptions.some((option) => option.value === currentYear);
      setSelectedYear(hasCurrentYear ? currentYear : yearOptions[0].value);
    }
  }, [yearOptions, selectedYear]);

  // Filter work periods by selected year
  const filteredWorkPeriods = useMemo(() => {
    if (!selectedYear) return workPeriods;

    return workPeriods.filter((workPeriod) => {
      return (
        formatYearDate(workPeriod.end_date) === selectedYear &&
        safeParseDateInput(workPeriod.end_date)?.isBefore(dayjs()) === true
      );
    });
  }, [workPeriods, selectedYear]);

  // Initialize budget values from work periods data
  useEffect(() => {
    const initialValues: Record<string, number | undefined> = {};
    filteredWorkPeriods.forEach((workPeriod) => {
      initialValues[workPeriod.id] = workPeriod.budget || undefined;
    });
    setBudgetValues(initialValues);
    setOriginalBudgetValues(initialValues);
    setHasChanges(false);
  }, [filteredWorkPeriods]);

  // Bulk update mutation
  const bulkUpdateMutation = useBulkUpdateWorkPeriods({
    subprojectId: selectedBoardId || '',
  });

  const handleBudgetChange = (workPeriodId: string, value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) || undefined : value;
    setBudgetValues((prev) => {
      const newValues = {
        ...prev,
        [workPeriodId]: numValue,
      };

      // Check if any value differs from original to determine hasChanges
      const hasAnyChanges = Object.keys(newValues).some((id) => newValues[id] !== originalBudgetValues[id]);
      setHasChanges(hasAnyChanges);

      return newValues;
    });
  };

  const handleSave = async () => {
    if (!selectedBoardId) {
      notifications.show({
        title: 'Error',
        message: 'Please select a board first',
        color: 'red',
      });
      return;
    }

    // Prepare payload for bulk update - only include changed values
    const payload: BulkUpdateWorkPeriodsPayload[] = filteredWorkPeriods
      .map((workPeriod) => ({
        id: workPeriod.id,
        budget: budgetValues[workPeriod.id] ?? 0,
      }))
      .filter((item) => {
        const currentValue = budgetValues[item.id];
        const originalValue = originalBudgetValues[item.id];

        // Include if the value has changed from the original
        return currentValue !== originalValue;
      });

    if (payload.length === 0) {
      notifications.show({
        title: 'No Changes',
        message: 'No budget values have been modified',
        color: 'orange',
      });
      return;
    }

    try {
      await bulkUpdateMutation.mutateAsync(payload);
      notifications.show({
        title: 'Success',
        message: `Updated ${payload.length} work period budget${payload.length === 1 ? '' : 's'} successfully`,
        color: 'green',
      });
      setHasChanges(false);
      // Update original values to reflect the new saved state
      setOriginalBudgetValues({ ...budgetValues });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update budget values',
        color: 'red',
      });
    }
  };

  return (
    <Box
      style={{
        padding: '16px 24px',
        marginBottom: '16px',
        backgroundColor: baseWhite,
        boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: viewAreaWidth - 80,
        overflow: 'hidden',
      }}
    >
      <Stack gap={16}>
        <Flex justify="space-between">
          <Group align="center" justify="flex-start">
            <Text size="large">Development spend</Text>
            <Text>Board</Text>
            <Select
              data={formattedBoards}
              value={selectedBoardId || null}
              onChange={(value: string | null) => handleBoardSelect(value)}
              placeholder="Select a board"
              searchable
            />
            <Text>Year</Text>
            <Select
              data={yearOptions}
              value={selectedYear}
              onChange={(value: string | null) => setSelectedYear(value)}
              placeholder="Select year"
              style={{ minWidth: 100 }}
            />
          </Group>
          <Group align="center">
            <Text>{`in ${currencySymbol} ${currencyCode}`}</Text>
            {hasChanges && (
              <Button onClick={handleSave} color={newCOLORS.indigo} loading={bulkUpdateMutation.isPending} size="sm">
                Save Changes
              </Button>
            )}
          </Group>
        </Flex>
        {filteredWorkPeriods.length ? (
          <SimpleGrid cols={{ base: 2, xs: 2, sm: 4, md: 5, lg: 6, xl: 7 }} spacing="sm" style={{ width: '100%' }}>
            {filteredWorkPeriods
              .sort((a, b) => {
                const aYear = formatYearDate(a.start_date);
                const bYear = formatYearDate(b.start_date);
                return aYear.localeCompare(bYear, 'en', { numeric: true });
              })
              .sort((a, b) => dayjs(a.start_date).unix() - dayjs(b.start_date).unix())
              .map((workPeriod) => (
                <Stack key={workPeriod.id} gap={0} align="center">
                  <Text weight="bold" style={{ textWrap: 'nowrap' }}>
                    {workPeriod.name}
                  </Text>
                  <Text
                    size="tiny"
                    style={{ textWrap: 'nowrap' }}
                  >{`${formatDate(workPeriod.start_date)} - ${formatDate(workPeriod.end_date)}`}</Text>
                  <NumberInput
                    hideControls
                    style={{ width: '200px' }}
                    radius="md"
                    disabled={dayjs(workPeriod.start_date).isAfter(dayjs())}
                    value={budgetValues[workPeriod.id] || ''}
                    onChange={(value) => handleBudgetChange(workPeriod.id, value)}
                    placeholder="Enter budget"
                    min={0}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    thousandSeparator=","
                  />
                </Stack>
              ))}
          </SimpleGrid>
        ) : (
          <Text>
            {workPeriods.length === 0
              ? 'No work periods found'
              : `No work periods found for ${selectedYear || 'selected year'}`}
          </Text>
        )}
      </Stack>
    </Box>
  );
}

export { EditFinancialDataFormBySprint };
